/**
 * Bundled sample IMMUNIZATIONS + WHO reference dose-forecast engine.
 *
 * Used by ImmunizationsPanel when the deployed site cannot reach the clinical
 * API: the section renders these contract-exact samples with a "Sample data"
 * badge (prov.sampleData), plus a dose forecast computed from the bundled
 * reference schedule below (subset of the prototype's VACCINES_SY).
 *
 * Row shapes mirror apps/api/app/schemas/immunization.py ImmunizationResponse
 * EXACTLY (cvx_code, vaccine_name, mvx_code, lot_number, date_administered,
 * report_date, administration_site, body_route, dose_number_in_series,
 * series_name, completion_status, source, status, smart_health_card_ref,
 * confidence_tier). No immunization router exists yet, so the panel attempts
 * the future `GET /api/v1/patients/{id}/immunizations → { items, total }`
 * contract first (ImmunizationListResponse) and falls back here on failure.
 *
 * GOVERNANCE — ported from the prototype (PHRPrototype_5.jsx, VACCINES_SY +
 * forecastNextDose): this schedule is a WHO reference table candidate for
 * Syria, NOT the confirmed Syrian MoH/EPI national schedule. Every
 * recommendation shown to the user carries the caveat banner ('imm.caveat');
 * the forecast is advisory only and nothing here should be read as, or
 * activated as, real clinical guidance until a human authority formally
 * approves the actual Syrian schedule. Where a vaccine has multiple WHO
 * schedule options only ONE representative option is encoded (same
 * simplification as the prototype).
 */

export type VaccineRule = {
  minAgeDays: number | null
  primaryDoses: number
  interval12: number | null
  interval23: number | null
  interval34: number | null
  booster: string
}

export type VaccineGroup = {
  code: string
  en: string
  ar: string
  /** CDC CVX codes covered by this antigen group (';'-separated). */
  cvx: string
  disease: string
  rule: VaccineRule
  /** Informal EN/AR aliases for name-based record matching. */
  keywords: string[]
}

export type ImmunizationView = {
  id: string
  patient_id: string
  cvx_code?: string | null
  vaccine_name?: string | null
  mvx_code?: string | null
  lot_number?: string | null
  date_administered?: string | null
  report_date?: string | null
  administration_site?: string | null
  body_route?: string | null
  dose_number_in_series?: number | null
  series_name?: string | null
  completion_status?: string | null // complete | partial | not-done | entered-in-error
  performer_id?: string | null
  source: string // clinic_report | patient_self_report | iis_feed
  status: string // completed | entered-in-error | not-done
  smart_health_card_ref?: string | null
  confidence_tier: number
  created_at?: string | null
  updated_at?: string | null
}

export type DoseForecast =
  | { status: 'seriesComplete'; boosterText?: string }
  | { status: 'dueNow'; nextDoseNumber: number }
  | { status: 'dueOn'; date: string; nextDoseNumber: number }
  | { status: 'notYetEligible'; date: string }

export type Recommendation = {
  group: VaccineGroup
  forecast: DoseForecast
}

/**
 * Reference schedule, subset of the prototype's VACCINES_SY (~8 of 26
 * antigen groups). Intervals are days between doses; `interval12` is the
 * minimum spacing between dose 1 and 2, and so on.
 */
export const VACCINE_SCHEDULE_SY: VaccineGroup[] = [
  {
    code: 'SY-VAC-BCG',
    en: 'BCG',
    ar: 'لقاح السل (BCG)',
    cvx: '19',
    disease: 'Tuberculosis',
    rule: { minAgeDays: 0, primaryDoses: 1, interval12: null, interval23: null, interval34: null, booster: 'None routinely' },
    keywords: ['bcg', 'سل', 'تدرن'],
  },
  {
    code: 'SY-VAC-HEPB',
    en: 'Hepatitis B',
    ar: 'لقاح التهاب الكبد B',
    cvx: '08;43;45',
    disease: 'Hepatitis B',
    rule: { minAgeDays: 0, primaryDoses: 3, interval12: 28, interval23: 28, interval34: 28, booster: 'No routine booster specified' },
    keywords: ['hepatitis b', 'hepb', 'hep b', 'التهاب الكبد'],
  },
  {
    code: 'SY-VAC-POLIO',
    en: 'Polio (OPV/IPV)',
    ar: 'لقاح شلل الأطفال (OPV/IPV)',
    cvx: '02;10;178;182;324',
    disease: 'Poliomyelitis',
    rule: { minAgeDays: 42, primaryDoses: 5, interval12: 28, interval23: 28, interval34: null, booster: 'None routinely' },
    keywords: ['polio', 'opv', 'ipv', 'شلل'],
  },
  {
    code: 'SY-VAC-DTP',
    en: 'DTP / Pentavalent (DTP-HepB-Hib)',
    ar: 'اللقاح الخماسي (الدفتيريا، الكزاز، السعال الديكي)',
    cvx: '01;20;115;113;09',
    disease: 'Diphtheria; Tetanus; Pertussis; Hepatitis B; Hib',
    rule: { minAgeDays: 42, primaryDoses: 3, interval12: 28, interval23: 28, interval34: null, booster: '3 boosters: 12–23 months; 4–7 years; 9–15 years' },
    keywords: ['dtp', 'pentavalent', 'penta', 'dtap', 'tdap', 'خماسي'],
  },
  {
    code: 'SY-VAC-MMR',
    en: 'MMR (Measles, Mumps, Rubella)',
    ar: 'لقاح الحصبة والنكاف والحصبة الألمانية (MMR)',
    cvx: '03;94;05;06;07',
    disease: 'Measles; Mumps; Rubella',
    rule: { minAgeDays: 274, primaryDoses: 2, interval12: 28, interval23: null, interval34: null, booster: 'None' },
    keywords: ['mmr', 'measles', 'mumps', 'rubella', 'حصبة', 'نكاف'],
  },
  {
    code: 'SY-VAC-PCV',
    en: 'Pneumococcal conjugate (PCV)',
    ar: 'لقاح المكورات الرئوية المقترن (PCV)',
    cvx: '133;177;215;216;152',
    disease: 'Pneumococcal disease',
    rule: { minAgeDays: 42, primaryDoses: 3, interval12: 28, interval23: 28, interval34: null, booster: '' },
    keywords: ['pneumococcal', 'pcv', 'مكورات رئوية'],
  },
  {
    code: 'SY-VAC-ROTA',
    en: 'Rotavirus',
    ar: 'لقاح الفيروس العجلي',
    cvx: '122;119;116',
    disease: 'Rotavirus gastroenteritis',
    rule: { minAgeDays: 42, primaryDoses: 2, interval12: 28, interval23: 28, interval34: null, booster: 'None' },
    keywords: ['rotavirus', 'rota', 'فيروس عجلي'],
  },
  {
    code: 'SY-VAC-INFLUENZA',
    en: 'Seasonal influenza',
    ar: 'لقاح الإنفلونزا الموسمية',
    cvx: '141;140;150;158;111;149;88',
    disease: 'Influenza',
    rule: { minAgeDays: 183, primaryDoses: 2, interval12: 28, interval23: null, interval34: null, booster: 'Revaccinate annually: 1 dose' },
    keywords: ['influenza', 'flu', 'انفلونزا', 'إنفلونزا'],
  },
]

const PATIENT_ID = '7f381bff-5fa9-4fee-92a0-f8e21a163e19'

/** DOB of the sample child — chosen so the demo forecast shows all statuses. */
export const SAMPLE_DOB = '2026-01-20'

/**
 * Contract-exact sample rows: a complete infancy primary-series story for a
 * child (BCG at birth, HepB 2/3, OPV 3/5, Penta complete, PCV mid-series,
 * Rotavirus complete incl. one patient self-report) written newest-last.
 */
export const SAMPLE_IMMUNIZATIONS: ImmunizationView[] = [
  {
    id: '9d000002-0000-4000-8000-000000000001',
    patient_id: PATIENT_ID,
    cvx_code: '19',
    vaccine_name: 'BCG vaccine',
    lot_number: 'BCG-SY-24-118',
    date_administered: '2026-01-20',
    report_date: '2026-01-20',
    administration_site: 'right-upper-arm',
    body_route: 'intradermal',
    dose_number_in_series: 1,
    series_name: 'BCG',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000002',
    patient_id: PATIENT_ID,
    cvx_code: '08',
    vaccine_name: 'Hepatitis B vaccine (pediatric)',
    lot_number: 'HEPB-SY-26-041',
    date_administered: '2026-01-20',
    report_date: '2026-01-20',
    administration_site: 'right-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 1,
    series_name: 'HepB',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000003',
    patient_id: PATIENT_ID,
    cvx_code: '02',
    vaccine_name: 'Poliomyelitis vaccine, oral (OPV)',
    lot_number: 'OPV-SY-26-012',
    date_administered: '2026-01-20',
    report_date: '2026-01-20',
    administration_site: 'oral',
    body_route: 'oral',
    dose_number_in_series: 1,
    series_name: 'Polio OPV',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000004',
    patient_id: PATIENT_ID,
    cvx_code: '08',
    vaccine_name: 'Hepatitis B vaccine (pediatric)',
    lot_number: 'HEPB-SY-26-118',
    date_administered: '2026-02-17',
    report_date: '2026-02-17',
    administration_site: 'left-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 2,
    series_name: 'HepB',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-00000000000b',
    patient_id: PATIENT_ID,
    cvx_code: '02',
    vaccine_name: 'Poliomyelitis vaccine, oral (OPV)',
    lot_number: 'OPV-SY-26-088',
    date_administered: '2026-03-21',
    report_date: '2026-03-21',
    administration_site: 'oral',
    body_route: 'oral',
    dose_number_in_series: 2,
    series_name: 'Polio OPV',
    completion_status: 'complete',
    source: 'iis_feed',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000005',
    patient_id: PATIENT_ID,
    cvx_code: '01',
    vaccine_name: 'DTP-HepB-Hib (pentavalent) vaccine',
    lot_number: 'PENTA-SY-26-201',
    date_administered: '2026-03-03',
    report_date: '2026-03-03',
    administration_site: 'left-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 1,
    series_name: 'DTP (Penta)',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000006',
    patient_id: PATIENT_ID,
    cvx_code: '133',
    vaccine_name: 'Pneumococcal conjugate vaccine (PCV13)',
    lot_number: 'PCV-SY-26-155',
    date_administered: '2026-03-03',
    report_date: '2026-03-03',
    administration_site: 'right-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 1,
    series_name: 'PCV',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000007',
    patient_id: PATIENT_ID,
    cvx_code: '122',
    vaccine_name: 'Rotavirus vaccine, monovalent (RV1)',
    lot_number: 'ROTA-SY-26-117',
    date_administered: '2026-03-03',
    report_date: '2026-03-03',
    administration_site: 'oral',
    body_route: 'oral',
    dose_number_in_series: 1,
    series_name: 'Rotavirus',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-00000000000c',
    patient_id: PATIENT_ID,
    cvx_code: '01',
    vaccine_name: 'DTP-HepB-Hib (pentavalent) vaccine',
    lot_number: 'PENTA-SY-26-233',
    date_administered: '2026-04-07',
    report_date: '2026-04-07',
    administration_site: 'left-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 2,
    series_name: 'DTP (Penta)',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-000000000008',
    patient_id: PATIENT_ID,
    cvx_code: '122',
    vaccine_name: 'Rotavirus vaccine, monovalent (RV1)',
    lot_number: 'ROTA-SY-26-180',
    date_administered: '2026-04-07',
    report_date: '2026-04-09',
    administration_site: 'oral',
    body_route: 'oral',
    dose_number_in_series: 2,
    series_name: 'Rotavirus',
    completion_status: 'complete',
    source: 'patient_self_report',
    status: 'completed',
    confidence_tier: 1,
  },
  {
    id: '9d000002-0000-4000-8000-000000000009',
    patient_id: PATIENT_ID,
    cvx_code: '01',
    vaccine_name: 'DTP-HepB-Hib (pentavalent) vaccine',
    lot_number: 'PENTA-SY-26-260',
    date_administered: '2026-05-05',
    report_date: '2026-05-05',
    administration_site: 'left-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 3,
    series_name: 'DTP (Penta)',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-00000000000d',
    patient_id: PATIENT_ID,
    cvx_code: '02',
    vaccine_name: 'Poliomyelitis vaccine, oral (OPV)',
    lot_number: 'OPV-SY-26-141',
    date_administered: '2026-05-16',
    report_date: '2026-05-16',
    administration_site: 'oral',
    body_route: 'oral',
    dose_number_in_series: 3,
    series_name: 'Polio OPV',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
  {
    id: '9d000002-0000-4000-8000-00000000000a',
    patient_id: PATIENT_ID,
    cvx_code: '133',
    vaccine_name: 'Pneumococcal conjugate vaccine (PCV13)',
    lot_number: 'PCV-SY-26-301',
    date_administered: '2026-08-10',
    report_date: '2026-08-10',
    administration_site: 'left-thigh',
    body_route: 'intramuscular',
    dose_number_in_series: 2,
    series_name: 'PCV',
    completion_status: 'complete',
    source: 'clinic_report',
    status: 'completed',
    confidence_tier: 3,
  },
]

/* ---------- schedule engine (ported from the prototype) ---------- */

function normalizeDate(value: string | null | undefined): Date | null {
  if (!value) return null
  const iso = value.length > 10 ? value.slice(0, 10) : value
  const d = new Date(`${iso}T00:00:00`)
  return Number.isNaN(d.getTime()) ? null : d
}

/** Does a given immunization record belong to this antigen group? */
export function recordLinkedToGroup(
  record: Pick<ImmunizationView, 'cvx_code' | 'vaccine_name'>,
  group: VaccineGroup,
): boolean {
  const cvx = (record.cvx_code ?? '').trim()
  if (cvx && group.cvx.split(';').map((c) => c.trim()).includes(cvx)) return true
  const name = (record.vaccine_name ?? '').toLowerCase()
  if (!name) return false
  if (name.includes(group.en.toLowerCase())) return true
  if (name.includes(group.ar.toLowerCase())) return true
  return group.keywords.some((k) => name.includes(k.toLowerCase()))
}

/**
 * Simplified next-dose forecast, WHO reference table only (see governance
 * note above). Given the patient's DOB and the doses already recorded for a
 * vaccine group, returns the same statuses as the prototype's engine:
 * 'dueNow' | 'dueOn' | 'notYetEligible' | 'seriesComplete'.
 */
export function forecastNextDose(
  group: VaccineGroup,
  dobIso: string,
  given: readonly Pick<ImmunizationView, 'cvx_code' | 'vaccine_name' | 'date_administered' | 'status'>[],
): DoseForecast | null {
  const rule = group.rule
  const dosesGivenSorted = given
    .filter((d) => d.status !== 'not-done' && d.status !== 'entered-in-error')
    .filter((d) => recordLinkedToGroup(d, group))
    .map((d) => ({ date: normalizeDate(d.date_administered) }))
    .filter((d): d is { date: Date } => d.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
  const doseCount = dosesGivenSorted.length

  if (rule.primaryDoses != null && doseCount >= rule.primaryDoses) {
    return { status: 'seriesComplete', boosterText: rule.booster }
  }

  const dob = new Date(`${dobIso.slice(0, 10)}T00:00:00`)
  const today = new Date()

  if (doseCount === 0) {
    if (rule.minAgeDays != null) {
      const eligibleDate = new Date(dob)
      eligibleDate.setDate(eligibleDate.getDate() + rule.minAgeDays)
      if (eligibleDate > today) {
        return { status: 'notYetEligible', date: eligibleDate.toISOString().slice(0, 10) }
      }
    }
    return { status: 'dueNow', nextDoseNumber: 1 }
  }

  // Determine the interval to the next dose based on which dose we're waiting for.
  const intervalByNextDose = { 2: rule.interval12, 3: rule.interval23, 4: rule.interval34 } as const
  const nextDoseNumber = doseCount + 1
  const intervalDays = intervalByNextDose[nextDoseNumber as 2 | 3 | 4]
  const lastDoseDate = dosesGivenSorted[dosesGivenSorted.length - 1].date

  if (intervalDays == null) {
    return { status: 'dueNow', nextDoseNumber }
  }
  const earliestNext = new Date(lastDoseDate)
  earliestNext.setDate(earliestNext.getDate() + intervalDays)

  if (earliestNext <= today) {
    return { status: 'dueNow', nextDoseNumber }
  }
  return { status: 'dueOn', date: earliestNext.toISOString().slice(0, 10), nextDoseNumber }
}

/**
 * One forecast per schedule group, dropping non-actionable rows exactly like
 * the prototype's Dashboard recommendations (a completed series with no
 * routine booster has nothing to show).
 */
export function buildRecommendations(
  dobIso: string | null | undefined,
  given: readonly ImmunizationView[],
): Recommendation[] {
  if (!dobIso) return []
  const out: Recommendation[] = []
  for (const group of VACCINE_SCHEDULE_SY) {
    const forecast = forecastNextDose(group, dobIso, given)
    if (!forecast) continue
    if (
      forecast.status === 'seriesComplete' &&
      (!forecast.boosterText ||
        forecast.boosterText === 'None routinely' ||
        forecast.boosterText === 'None')
    ) {
      continue // nothing actionable to show
    }
    out.push({ group, forecast })
  }
  return out
}

/** Bilingual display name for a given-dose row (master list, then fallbacks). */
export function vaccineDisplayFor(
  record: Pick<ImmunizationView, 'cvx_code' | 'vaccine_name'>,
): { en: string; ar: string; groupCode: string | null } {
  const group = VACCINE_SCHEDULE_SY.find((g) => recordLinkedToGroup(record, g))
  if (group) return { en: group.en, ar: group.ar, groupCode: group.code }
  const fallback = record.vaccine_name ?? (record.cvx_code ? `CVX ${record.cvx_code}` : '')
  return { en: fallback, ar: fallback, groupCode: null }
}